import { ABExplorationKeyWords } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { IMeasureType, IMetricBatchEvaluationResult } from "@src/types/ab-testing-exploration";
import { getMetricEvaluationRequireMeasures } from "../report-data/get-metric-evaluation-require-measures";
import { AbExplorationEvaluateMetricProps } from "@src/hooks/ab-testing/explore-evaluate";
import { convertMetricConversionRateToMetricSampleSize } from "./manage-state-event-lists";

export class MappingMetricEvaluate {
    private loadResult: Record<string, any>[] = [];
    private baseVariant = "";
    private variants: string[] = [];
    private measureValue = "";
    private significanceLevel = 0;
    private measureType: IMeasureType;
    private breakdownBy: string[] | null = null;

    constructor({
        breakdownBy,
        baseVariant,
        measureValue,
        significanceLevel,
        variants,
        measureType,
    }: {
        breakdownBy: string[] | null;
        baseVariant: string;
        measureValue: string;
        significanceLevel: number;
        variants: string[];
        measureType?: IMeasureType;
    }) {
        this.baseVariant = baseVariant;
        this.measureValue = measureValue;
        this.breakdownBy = breakdownBy;
        this.significanceLevel = significanceLevel;
        this.variants = variants;
        this.measureType = measureType ?? "proportion";
    }

    public setLoadResult(loadResult: Record<string, any>[]) {
        this.loadResult = loadResult;
    }
    private getBatchId(record: Record<string, any>, keys?: string[] | null) {
        return keys?.map((k) => record[k]).join("-") || "-";
    }

    private groupedLoadResultByBreakdown() {
        if (!this.loadResult) return {};
        return Object.groupBy(this.loadResult, (i) => this.getBatchId(i, this.breakdownBy));
    }
    private evaluatingData(measureValue: string | { customMean: string; customNobs?: string; customStd?: string }) {
        const { toEvaluatingData } = getMetricEvaluationRequireMeasures(
            measureValue,
            this.measureType,
            undefined,
            true,
            true,
        );
        return toEvaluatingData;
    }

    public getMetricEvaluateRequest(
        sampleSize?: { getOriginalSampleSize: boolean },
        customMeasures?: { customMean: string; customNobs?: string; customStd?: string },
    ): AbExplorationEvaluateMetricProps {
        const groupedDetailData = this.groupedLoadResultByBreakdown();

        const batchData = Object.keys(groupedDetailData)
            .filter((batchId: string) => {
                const baseVariantReportIsMissing =
                    groupedDetailData[batchId]?.findIndex(({ exp_group }) => exp_group === this.baseVariant) === -1;
                const isNotEnoughVariantReportToCompare =
                    groupedDetailData[batchId] === undefined || groupedDetailData[batchId]?.length <= 1;

                return !isNotEnoughVariantReportToCompare && !baseVariantReportIsMissing;
            })
            .map((batchId: string) => {
                return {
                    batch_id: batchId,
                    data: groupedDetailData[batchId]
                        ?.map(({ [ABExplorationKeyWords.VARIANT]: variantName, ...rest }) => {
                            if (!this.measureValue) return null;
                            const toEvaluatingData = customMeasures
                                ? this.evaluatingData(customMeasures)
                                : this.evaluatingData(this.measureValue);
                            if (sampleSize?.getOriginalSampleSize) {
                                return toEvaluatingData(variantName, rest);
                            }
                            return toEvaluatingData(variantName, {
                                ...rest,
                                [ABExplorationKeyWords.SAMPLE_SIZE]:
                                    rest[convertMetricConversionRateToMetricSampleSize(this.measureValue)],
                            });
                        })
                        .filter((d) => !!d),
                };
            })
            .filter(({ data }) => !!data && data.length >= 2) // there is not enough data to evaluate
            .filter(({ data }) => data?.findIndex(({ exp_group }) => exp_group === this.baseVariant) !== -1); // invalid base report

        return {
            baseGroup: this.baseVariant ?? "",
            variantGroups: this.variants || [],
            metric: this.measureValue ?? "",
            metricType: this.measureType,
            significanceLevel: this.significanceLevel ?? 0,
            data: batchData,
            batch: true,
            isSkip: false,
        };
    }

    private groupEvaluateResultByBreakdownDim(metricEvaluationResult: IMetricBatchEvaluationResult[]) {
        const groupedByBreakdown = this.groupedLoadResultByBreakdown();
        return Object.keys(groupedByBreakdown)
            .map((batchId) => {
                const metricEvaluationReport = metricEvaluationResult.find(
                    (rec: any) => (rec as IMetricBatchEvaluationResult).batch_id === batchId,
                ) as IMetricBatchEvaluationResult;
                return (
                    groupedByBreakdown[batchId]?.map((rec) => {
                        const metricEvaluation =
                            metricEvaluationReport?.data.find(
                                ({ variant_group }) => variant_group === rec[ABExplorationKeyWords.VARIANT],
                            ) || {};
                        const toEvaluatingData = this.evaluatingData(this.measureValue ?? "");
                        return {
                            ...rec,
                            ...metricEvaluation,
                            // evaluation api return measure as 0 when error throws
                            // then we need to use measures in record to reduce as much as loss
                            ...toEvaluatingData(rec[ABExplorationKeyWords.VARIANT], {
                                ...rec,
                                [ABExplorationKeyWords.SAMPLE_SIZE]:
                                    rec[convertMetricConversionRateToMetricSampleSize(this.measureValue)],
                            }),
                            [ABExplorationKeyWords.SAMPLE_SIZE]:
                                rec[convertMetricConversionRateToMetricSampleSize(this.measureValue)],
                        };
                    }) || []
                );
            })
            .reduce((prev, curr) => [...prev, ...curr], []);
    }

    private formatToDataSource(dataSource: any[]) {
        if (dataSource === undefined) return [];
        const groupedData = Object.groupBy(dataSource, (i) => this.getBatchId(i, this.breakdownBy));

        return Object.keys(groupedData).map((batchId) => {
            const records = groupedData[batchId] || [];
            return {
                batchId,
                batchIdentifiers:
                    this.breakdownBy
                        ?.map((k: any) => {
                            if (records === undefined || records.length === 0) return {};

                            return {
                                [k]: records[0][k],
                            };
                        })
                        .reduce((prev: any, curr: any) => Object.assign(prev, curr), {}) || {},
                // variants: records
                variants: records
                    .map((rec) => {
                        return {
                            [rec[ABExplorationKeyWords.VARIANT]]: {
                                ...rec,
                            },
                        };
                    })
                    .reduce((prev, curr) => Object.assign(prev, curr), {}),
            };
        });
    }
    public getDataSource(metricEvaluationResult: any) {
        return this.formatToDataSource(this.groupEvaluateResultByBreakdownDim(metricEvaluationResult));
    }
}
